import { useTheme } from '@mui/material';
import {
    DeleteOutline as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import Box from '@/components/ui/Box/Box';
import Card from '@/components/ui/Card/Card';
import Divider from '@/components/ui/Divider/Divider';
import IconButton from '@/components/ui/IconButton/IconButton';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton';
import Paper from '@/components/ui/Paper/Paper';
import Typography from '@/components/ui/Typography/Typography';
import { useCompany } from '@/providers/CompanyProvider/CompanyProvider';
import { useNewJob } from '@/providers/NewJobProvider/NewJobProvider';
import type { NewJobCartProps } from './INewJobCart';
import { styles } from './styles';

const formatCurrency = (n: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);

const NewJobCart = ({
    submitting,
    onSubmit,
    onEditService,
}: NewJobCartProps) => {
    const theme = useTheme();
    const newJob = useNewJob();
    const companyCtx = useCompany();
    const isRepairer = companyCtx.state.company?.marketplace_side === 'Repairer';

    const total = newJob.state.services.reduce((sum, s) => {
        const qty = Math.max(s.parts.length, 1);
        if (s.priceEach) {
            return sum + s.prices.reduce((acc, p) => acc + (parseFloat(p) || 0), 0);
        }
        return sum + (parseFloat(s.price) || 0) * qty;
    }, 0);

    return (
        <Paper sx={styles.paper}>
            <Box sx={styles.header}>
                <Typography sx={styles.title}>Summary</Typography>
            </Box>
            <LoadingButton
                variant="contained"
                loading={submitting}
                onClick={onSubmit}
                sx={styles.submitBtn(theme)}
                disabled={!newJob.state.services.length}
            >
                <Box sx={styles.submitRow}>
                    <Typography sx={styles.submitLabel}>Submit Request</Typography>
                    <Typography sx={styles.submitTotal}>{formatCurrency(total)}</Typography>
                </Box>
            </LoadingButton>
            <Divider />
            {newJob.state.services.map((service) => (
                <Card key={service.id} sx={styles.serviceCard(theme)}>
                    <Box sx={styles.serviceRow}>
                        <Box>
                            <Typography sx={styles.serviceType}>
                                {service.type || 'Service'}
                            </Typography>
                            <Typography sx={styles.serviceMeta}>
                                {`${Math.max(service.parts.length, 1)} Part(s)`}
                            </Typography>
                            {!isRepairer && service.repairer?.name && (
                                <Typography sx={styles.serviceMeta}>
                                    {service.repairer.name}
                                </Typography>
                            )}
                            {isRepairer && newJob.state.supplier && (
                                <Typography sx={styles.serviceMeta}>
                                    {newJob.state.supplier.name}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={styles.serviceActions}>
                            <IconButton
                                size="small"
                                onClick={() => onEditService(service.id)}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() =>
                                    newJob.DISPATCH({
                                        type: 'REMOVE_SERVICE',
                                        payload: service.id,
                                    })
                                }
                            >
                                <DeleteIcon fontSize="small" sx={{ color: 'red' }} />
                            </IconButton>
                        </Box>
                    </Box>
                    <Typography sx={styles.serviceTotal}>{service.totalDP}</Typography>
                </Card>
            ))}
        </Paper>
    );
};

export default NewJobCart;
